/* ============================================================================
   Bulk tour import.

   One row is one SHOW. Tours are inferred by grouping rows that share a tour
   name, and a tour's dates are the first and last show in it unless they're
   given explicitly. That shape was chosen because it is the one people already
   have: an advance sheet, a routing spreadsheet and a promoter's confirmation
   list are all one-line-per-date, and asking someone to normalise into two
   sheets before they can import is how an import feature goes unused.

   Everything except the four required columns is optional, and unknown columns
   are ignored rather than rejected — a spreadsheet with a "fee" column and a
   "notes to self" column should still import.

   The parser is deliberately hand-rolled. It has to handle quoted fields with
   commas and newlines in them, which every real venue address eventually has,
   and that is about forty lines. A dependency for forty lines that never
   change is a dependency that eventually breaks a build for no reason.
   ============================================================================ */

export interface CsvShow {
  date: string
  city: string
  country: string
  venueName: string
  venueAddress: string
  timezone: string
  loadIn: string
  soundCheck: string
  doorsOpen: string
  headlinerSetTime: string
  packOut: string
  contactName: string
  contactEmail: string
  contactPhone: string
}

export interface CsvTour {
  key: string
  tourName: string
  startDate: string
  endDate: string
  shows: CsvShow[]
}

export interface ParseResult {
  tours: CsvTour[]
  rows: number
  /** Row-level complaints, with the line number people see in their editor. */
  problems: Array<{ line: number; message: string }>
}

/* ------------------------------------------------------------- the shape ---- */

interface Column {
  /** Canonical name, and the header written into the template. */
  key: string
  required?: boolean
  /** Everything we'll accept as this column, lowercased and stripped. */
  aliases: string[]
  example: string
  note: string
}

export const COLUMNS: Column[] = [
  {
    key: 'tour_name',
    required: true,
    aliases: ['tour', 'tourname', 'tour name'],
    example: 'Southern Skies',
    note: 'Rows sharing a name become one tour.',
  },
  {
    key: 'show_date',
    required: true,
    aliases: ['date', 'showdate', 'show date'],
    example: '2026-11-06',
    note: 'YYYY-MM-DD. Also accepts DD/MM/YYYY.',
  },
  {
    key: 'city',
    required: true,
    aliases: ['town'],
    example: 'Berkeley',
    note: '',
  },
  {
    key: 'venue_name',
    required: true,
    aliases: ['venue', 'venuename', 'venue name'],
    example: 'The UC Theatre',
    note: '',
  },
  {
    key: 'country',
    aliases: [],
    example: 'United States',
    note: '',
  },
  {
    key: 'venue_address',
    aliases: ['address'],
    example: '2036 University Ave, Berkeley CA',
    note: 'Quote it if it contains a comma.',
  },
  {
    key: 'timezone',
    aliases: ['tz', 'time zone'],
    example: 'America/Los_Angeles',
    note: 'IANA name. Left blank, Encore works it out from the venue.',
  },
  { key: 'load_in', aliases: ['loadin', 'load in'], example: '15:00', note: '24-hour, venue local.' },
  { key: 'soundcheck', aliases: ['sound check'], example: '17:00', note: '' },
  { key: 'doors', aliases: ['doors_open', 'doorsopen'], example: '19:00', note: '' },
  { key: 'set_time', aliases: ['set', 'headliner', 'headliner_set'], example: '20:30', note: '' },
  { key: 'pack_out', aliases: ['packout', 'load out', 'loadout'], example: '23:30', note: '' },
  { key: 'contact_name', aliases: ['promoter', 'contact'], example: 'Dana Iva', note: '' },
  { key: 'contact_email', aliases: ['email'], example: 'dana@ucheatre.org', note: '' },
  { key: 'contact_phone', aliases: ['phone'], example: '+1 510 555 0134', note: '' },
  {
    key: 'tour_start',
    aliases: ['start', 'start_date'],
    example: '',
    note: 'Optional. Defaults to the first show.',
  },
  {
    key: 'tour_end',
    aliases: ['end', 'end_date'],
    example: '',
    note: 'Optional. Defaults to the last show.',
  },
]

/** The file the Download template button produces. */
export function templateCsv(): string {
  const header = COLUMNS.map((column) => column.key).join(',')

  const example = (tour: string, date: string, city: string, venue: string, address: string) =>
    [
      tour, date, city, 'United States', venue, address, '',
      '15:00', '17:00', '19:00', '20:30', '23:30',
      '', '', '', '', '',
    ]
      .map(quote)
      .join(',')

  return [
    header,
    example('Southern Skies', '2026-11-06', 'Berkeley', 'The UC Theatre', '2036 University Ave, Berkeley CA'),
    example('Southern Skies', '2026-11-08', 'Portland', 'Roseland Theater', '8 NW 6th Ave, Portland OR'),
    example('Southern Skies', '2026-11-09', 'Seattle', 'The Showbox', '1426 1st Ave, Seattle WA'),
  ].join('\n')
}

function quote(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/* ---------------------------------------------------------------- parser ---- */

/**
 * Splits CSV text into rows of cells.
 *
 * Handles quoted fields containing commas, quoted fields containing newlines,
 * doubled quotes as an escape, and both line endings. Everything a venue
 * address does eventually.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false

  const body = text.replace(/^﻿/, '') // Excel writes a byte-order mark.

  for (let index = 0; index < body.length; index++) {
    const character = body[index]

    if (quoted) {
      if (character === '"') {
        if (body[index + 1] === '"') {
          cell += '"'
          index++
        } else {
          quoted = false
        }
      } else {
        cell += character
      }
      continue
    }

    if (character === '"') {
      quoted = true
    } else if (character === ',') {
      row.push(cell)
      cell = ''
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && body[index + 1] === '\n') index++
      row.push(cell)
      // A blank line is a blank line, not an empty show.
      if (row.some((value) => value.trim())) rows.push(row)
      row = []
      cell = ''
    } else {
      cell += character
    }
  }

  row.push(cell)
  if (row.some((value) => value.trim())) rows.push(row)

  return rows
}

function normalise(header: string): string {
  return header.trim().toLowerCase().replace(/[\s_-]+/g, '')
}

/** Maps whatever the file calls its columns onto ours. */
function mapHeaders(header: string[]): Record<string, number> {
  const index: Record<string, number> = {}

  header.forEach((raw, position) => {
    const cleaned = normalise(raw)
    const column = COLUMNS.find(
      (candidate) =>
        normalise(candidate.key) === cleaned ||
        candidate.aliases.some((alias) => normalise(alias) === cleaned),
    )
    if (column && index[column.key] === undefined) index[column.key] = position
  })

  return index
}

/**
 * Dates people actually type.
 *
 * ISO first because it's unambiguous. `DD/MM/YYYY` next because the rest of the
 * product is written for people outside the US — and `03/04/2026` genuinely
 * cannot be resolved without picking a side, so this picks the one that matches
 * every other date in the app.
 */
function parseDate(raw: string): string | null {
  const value = raw.trim()
  if (!value) return null

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value)
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`

  const slashed = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/.exec(value)
  if (slashed) {
    return `${slashed[3]}-${slashed[2].padStart(2, '0')}-${slashed[1].padStart(2, '0')}`
  }

  // Anything else the browser can read, e.g. "6 November 2026".
  const parsed = new Date(value)
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(
      parsed.getDate(),
    ).padStart(2, '0')}`
  }

  return null
}

/** "7pm", "19:00", "1900" → "19:00". Empty stays empty. */
function parseTime(raw: string): string {
  const value = raw.trim().toLowerCase()
  if (!value) return ''

  const ampm = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/.exec(value)
  if (ampm) {
    let hour = Number(ampm[1]) % 12
    if (ampm[3] === 'pm') hour += 12
    return `${String(hour).padStart(2, '0')}:${ampm[2] ?? '00'}`
  }

  const colon = /^(\d{1,2}):(\d{2})/.exec(value)
  if (colon) return `${colon[1].padStart(2, '0')}:${colon[2]}`

  const bare = /^(\d{2})(\d{2})$/.exec(value)
  if (bare) return `${bare[1]}:${bare[2]}`

  return ''
}

export function parseTourCsv(text: string): ParseResult {
  const rows = parseCsv(text)
  const problems: ParseResult['problems'] = []

  if (rows.length < 2) {
    return { tours: [], rows: 0, problems: [{ line: 1, message: 'The file has no rows.' }] }
  }

  const index = mapHeaders(rows[0])
  const missing = COLUMNS.filter((column) => column.required && index[column.key] === undefined)
  if (missing.length > 0) {
    return {
      tours: [],
      rows: 0,
      problems: [
        {
          line: 1,
          message: `Missing required column${missing.length === 1 ? '' : 's'}: ${missing
            .map((column) => column.key)
            .join(', ')}.`,
        },
      ],
    }
  }

  const get = (row: string[], key: string) => (row[index[key]] ?? '').trim()
  const byTour = new Map<string, CsvTour>()

  rows.slice(1).forEach((row, offset) => {
    const line = offset + 2 // 1-indexed, and the header is line 1.

    const tourName = get(row, 'tour_name')
    const city = get(row, 'city')
    const venueName = get(row, 'venue_name')
    const date = parseDate(get(row, 'show_date'))

    if (!tourName) return problems.push({ line, message: 'No tour name.' })
    if (!date) {
      return problems.push({
        line,
        message: `Couldn't read the date "${get(row, 'show_date')}".`,
      })
    }
    if (!city && !venueName) {
      return problems.push({ line, message: 'Needs at least a city or a venue.' })
    }

    const show: CsvShow = {
      date,
      city,
      country: get(row, 'country'),
      venueName,
      venueAddress: get(row, 'venue_address'),
      timezone: get(row, 'timezone'),
      loadIn: parseTime(get(row, 'load_in')),
      soundCheck: parseTime(get(row, 'soundcheck')),
      doorsOpen: parseTime(get(row, 'doors')),
      headlinerSetTime: parseTime(get(row, 'set_time')),
      packOut: parseTime(get(row, 'pack_out')),
      contactName: get(row, 'contact_name'),
      contactEmail: get(row, 'contact_email'),
      contactPhone: get(row, 'contact_phone'),
    }

    const existing = byTour.get(tourName)
    if (existing) {
      existing.shows.push(show)
    } else {
      byTour.set(tourName, {
        // Prefixed so the importer can tell a CSV tour from a scanned one and
        // keep both idempotent against re-runs.
        key: `csv:${tourName}`,
        tourName,
        startDate: parseDate(get(row, 'tour_start')) ?? date,
        endDate: parseDate(get(row, 'tour_end')) ?? date,
        shows: [show],
      })
    }
  })

  const tours = [...byTour.values()].map((tour) => {
    const sorted = [...tour.shows].sort((a, b) => a.date.localeCompare(b.date))
    return {
      ...tour,
      shows: sorted,
      // Explicit tour dates win; otherwise the run is as long as its shows.
      startDate:
        tour.startDate < sorted[0].date ? tour.startDate : sorted[0].date,
      endDate:
        tour.endDate > sorted[sorted.length - 1].date
          ? tour.endDate
          : sorted[sorted.length - 1].date,
    }
  })

  return {
    tours: tours.sort((a, b) => b.startDate.localeCompare(a.startDate)),
    rows: rows.length - 1,
    problems,
  }
}

/** Triggers a download without a server round trip or a dependency. */
export function downloadTemplate() {
  const blob = new Blob([templateCsv()], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'encore-tours-template.csv'
  anchor.click()
  URL.revokeObjectURL(url)
}
